import { KeyboardEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationManager } from 'react-notifications';
import Field from '../../components/shared/Field/Field';
import { EvaluationTagDto, serviceEvaluations } from '../../services/service-evaluations';
import { EvaluationErrors, EvaluationLimits } from '../../util/evaluationContract';
import { reportUnexpected } from '../page-supplier-evaluations/reportUnexpected';
import { errorKeyOf, evaluationErrorMessage } from './evaluationStart';
import styles from './app-evaluation-start.module.scss';

export type TagManagerProps = {
  tags: EvaluationTagDto[];
  onTagsChange: (tags: EvaluationTagDto[]) => void;
  onArchived: (tagId: string) => void;
};

const INLINE_ERRORS: string[] = [EvaluationErrors.TagNameTaken, EvaluationErrors.TagNameInvalid];

const TagManager = (props: TagManagerProps) => {
  const { tags } = props;
  const [t] = useTranslation('evaluations');
  const latest = useRef(props);
  latest.current = props;
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const replace = (updated: EvaluationTagDto) =>
    latest.current.onTagsChange(
      latest.current.tags.map(tag => (tag._id === updated._id ? updated : tag)),
    );

  const setError = (tagId: string, message: string | null) =>
    setErrors(current => {
      const next = { ...current };
      if (message) next[tagId] = message;
      else delete next[tagId];
      return next;
    });

  const run = async (
    tagId: string,
    call: () => Promise<EvaluationTagDto>,
    onDone: (tag: EvaluationTagDto) => void,
  ) => {
    if (busyId) return;
    setBusyId(tagId);
    setError(tagId, null);
    try {
      const updated = await call();
      replace(updated);
      onDone(updated);
    } catch (error) {
      reportUnexpected(error);
      const message = evaluationErrorMessage(t, error);
      const key = errorKeyOf(error);
      if (message && key && INLINE_ERRORS.includes(key)) setError(tagId, message);
      else if (message) NotificationManager.error(message);
    } finally {
      setBusyId(null);
    }
  };

  const startRename = (tag: EvaluationTagDto) => {
    setRenamingId(tag._id);
    setDraft(tag.name);
    setError(tag._id, null);
  };

  const stopRename = (tagId: string) => {
    setRenamingId(null);
    setError(tagId, null);
  };

  const saveRename = (tag: EvaluationTagDto) => {
    const name = draft.trim();
    if (!name || name === tag.name) {
      stopRename(tag._id);
      return;
    }
    run(
      tag._id,
      () => serviceEvaluations.renameTag(tag._id, name),
      () => {
        setRenamingId(null);
        NotificationManager.success(
          t(
            'tags.renamedEverywhere',
            'Taggen har fått nytt namn. Det nya namnet syns på alla utvärderingar som har taggen.',
          ),
        );
      },
    );
  };

  const archive = (tag: EvaluationTagDto) =>
    run(
      tag._id,
      () => serviceEvaluations.archiveTag(tag._id),
      () => {
        latest.current.onArchived(tag._id);
        NotificationManager.success(
          t(
            'tags.archivedEverywhere',
            'Taggen är arkiverad. Den finns kvar där den redan används men kan inte väljas för nya utvärderingar.',
          ),
        );
      },
    );

  const restore = (tag: EvaluationTagDto) =>
    run(
      tag._id,
      () => serviceEvaluations.unarchiveTag(tag._id),
      () =>
        NotificationManager.success(
          t('tags.restoredSelectable', 'Taggen är återställd och kan väljas igen.'),
        ),
    );

  const active = tags.filter(tag => !tag.archived);
  const archived = tags.filter(tag => tag.archived);

  const rowError = (tagId: string) =>
    errors[tagId] && (
      <p
        id={`tag-error-${tagId}`}
        className={`${styles.hint} ${styles.invalid} ${styles.rowError}`}
      >
        {errors[tagId]}
      </p>
    );

  const quietButton = (label: string, onClick: () => void, tagId: string, disabled = false) => (
    <button
      type="button"
      className={styles.quietAction}
      disabled={disabled || busyId === tagId}
      aria-busy={busyId === tagId}
      onClick={onClick}
    >
      {label}
    </button>
  );

  const renameRow = (tag: EvaluationTagDto) => (
    <>
      <div className={styles.renameRow}>
        <div className={styles.renameInput}>
          <Field
            value={draft}
            onChange={text => {
              setDraft(text);
              setError(tag._id, null);
            }}
            ariaDescribedBy={errors[tag._id] ? `tag-error-${tag._id}` : undefined}
            className={`${styles.renameField} ${errors[tag._id] ? styles.renameFieldError : ''}`}
            onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
              if (event.key === 'Enter') saveRename(tag);
              if (event.key === 'Escape') stopRename(tag._id);
            }}
          />
        </div>
        {quietButton(
          t('tags.save', 'Spara'),
          () => saveRename(tag),
          tag._id,
          draft.trim().length > EvaluationLimits.TagNameMaxLength,
        )}
        {quietButton(t('tags.cancel', 'Avbryt'), () => stopRename(tag._id), tag._id)}
      </div>
      {rowError(tag._id)}
    </>
  );

  return (
    <div className={styles.tagManager}>
      <div className={styles.tagLists}>
        {active.length ? (
          <ul className={styles.tagList}>
            {active.map(tag => (
              <li key={tag._id} className={styles.tagListItem}>
                {renamingId === tag._id ? (
                  renameRow(tag)
                ) : (
                  <>
                    <span data-tag-name="" className={styles.tagName} title={tag.name}>
                      {tag.name}
                    </span>
                    {quietButton(t('tags.rename', 'Byt namn'), () => startRename(tag), tag._id)}
                    {quietButton(t('tags.archive', 'Arkivera'), () => archive(tag), tag._id)}
                  </>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.hint}>{t('tags.none', 'Inga aktiva taggar.')}</p>
        )}
        {archived.length > 0 && (
          <>
            <p className={styles.groupHeading}>{t('tags.archivedHeading', 'Arkiverade')}</p>
            <ul className={styles.tagList}>
              {archived.map(tag => (
                <li key={tag._id} className={styles.tagListItem}>
                  <span data-tag-name="" className={styles.tagName} title={tag.name}>
                    {tag.name}
                  </span>
                  {quietButton(t('tags.restore', 'Återställ'), () => restore(tag), tag._id)}
                  {rowError(tag._id)}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

export default TagManager;
